/**
 * PrintableFisa — pagină „Fișă tip pentru completare manuală".
 *
 * Ruta: /admin/patients/:id/fisa-tip-print
 *
 * Conținut:
 *  - Antet cu Datele Personale (nume, prenume, CNP, dată naștere, sex, sport, club, coach)
 *  - Antecedente Familiale — doar problemele marcate „Da" în formular
 *  - Antecedente Personale + alte flag-uri „Da"
 *  - Efort Fizic — doar schimbări/simptome marcate „Da"
 *  - Ateliere 1-4: câmpuri goale cu linii pentru scris manual
 *  - Atelier 4: checkbox-uri „Asimptomatic" + „Normal clinic"
 *
 * Se deschide într-o fereastră nouă și utilizatorul apasă Ctrl+P pentru print A4.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { patientApi } from "../services/api";

const Line = ({ w = "100%" }) => (
  <span className="fisa-line" style={{ minWidth: w }} />
);

// Rând tip formular: etichetă + linie de scris manual.
const Row = ({ label, w = "60%" }) => (
  <div className="fisa-row">
    <span className="fisa-label">{label}:</span>
    <Line w={w} />
  </div>
);

// Casetă de bifat (goală, se completează manual)
const CheckBox = ({ label }) => (
  <span className="fisa-cb">
    <span className="cb-box" />
    <span className="cb-lbl">{label}</span>
  </span>
);

const H1 = ({ children }) => <h1 className="fisa-h1">{children}</h1>;
const H2 = ({ children }) => <h2 className="fisa-h2">{children}</h2>;
const H3 = ({ children }) => <h3 className="fisa-h3">{children}</h3>;

const dashIfEmpty = (v) => (v === null || v === undefined || v === "" ? "—" : v);

const isDa = (v) => String(v || "").toLowerCase() === "da";

const PrintableFisa = () => {
  const { id } = useParams();
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await patientApi.getById(id);
        if (mounted) setPatient(data);
      } catch (err) {
        console.error("Failed to load patient:", err);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [id]);

  if (loading) {
    return (
      <div style={{ padding: 40, fontFamily: "serif" }}>Se încarcă fișa…</div>
    );
  }
  if (!patient) {
    return (
      <div style={{ padding: 40, fontFamily: "serif" }}>
        Pacientul nu a putut fi încărcat.
      </div>
    );
  }

  const dp = patient.date_personale || {};
  const is = patient.info_sportive || {};
  const im = patient.istoric_medical || {};
  const ef = patient.efort_fizic || {};

  // Construim liste „x roșu": doar întrebările marcate Da (pentru semnalare medicului
  // care completează fișa manual)
  const antecedFamilie = [];
  if (isDa(im.antecedente_familie)) {
    const tipuri = im.antecedente_familie_tipuri || [];
    if (tipuri.length) antecedFamilie.push(...tipuri);
    if (im.antecedente_familie_detalii) antecedFamilie.push(im.antecedente_familie_detalii);
  }

  const antecedPersonale = [];
  const persoFlags = [
    ["antecedente_personale", "Antecedente personale"],
    ["modificari_sanatate", "Modificări stare de sănătate"],
    ["accidentari_sportive", "Accidentări sportive"],
    ["internari_medicale", "Internări medicale"],
    ["tratament_medicamentos", "Tratament medicamentos în curs"],
  ];
  persoFlags.forEach(([k, lbl]) => {
    if (isDa(im[k])) {
      const detalii = im[`${k}_detalii`];
      antecedPersonale.push(detalii ? `${lbl}: ${detalii}` : lbl);
    }
  });

  const efortFlags = [];
  if (isDa(ef.schimbari_efort)) {
    const tipuri = ef.schimbari_efort_tipuri || [];
    if (tipuri.length) efortFlags.push(...tipuri);
    if (ef.schimbari_efort_detalii) efortFlags.push(ef.schimbari_efort_detalii);
  }
  if (isDa(ef.oboseste_repede)) efortFlags.push("Obosește repede la efort");

  return (
    <>
      <style>{`
        @page { size: A4; margin: 12mm; }
        html, body { background: #fff; color: #111; }
        body { font-family: 'Georgia', 'Times New Roman', serif; font-size: 11pt; }
        .fisa-wrap { max-width: 820px; margin: 0 auto; padding: 8mm 4mm; }
        .fisa-print-actions { position: sticky; top: 8px; text-align: right; margin-bottom: 8px; }
        .fisa-print-actions button { padding: 6px 14px; font-size: 12px; cursor: pointer;
          background: #1e293b; color: #fff; border: 0; border-radius: 4px; }
        @media print { .fisa-print-actions { display: none; } .fisa-wrap { padding: 0; } }
        .fisa-h1 { font-size: 15pt; text-align: center; text-transform: uppercase; margin: 4pt 0 2pt; letter-spacing: .5pt; }
        .fisa-h2 { font-size: 12pt; text-transform: uppercase; margin: 10pt 0 3pt; border-bottom: 1.5pt solid #111; padding-bottom: 2pt; letter-spacing: .3pt; }
        .fisa-h3 { font-size: 11pt; margin: 6pt 0 3pt; font-weight: 700; }
        .fisa-sub { text-align: center; font-size: 10pt; margin-bottom: 6pt; color: #333; }
        .fisa-row { display: flex; align-items: baseline; margin: 3pt 0; gap: 6pt; flex-wrap: wrap; }
        .fisa-label { font-weight: 700; white-space: nowrap; }
        .fisa-line { display: inline-block; border-bottom: 1pt solid #333; flex: 1; min-height: 12pt; }
        .fisa-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 4pt 16pt; }
        .fisa-grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 4pt 16pt; }
        .fisa-cb { display: inline-flex; align-items: center; gap: 5pt; margin-right: 12pt; font-size: 11pt; }
        .cb-box { display: inline-block; width: 12pt; height: 12pt; border: 1.2pt solid #111; border-radius: 2pt; }
        .cb-lbl { font-weight: 500; }
        .fisa-alert { background: #fff5f5; border: 1pt solid #e11d48; border-left: 4pt solid #e11d48; padding: 5pt 8pt; margin: 4pt 0; }
        .fisa-alert-title { font-weight: 700; color: #b91c1c; font-size: 10pt; text-transform: uppercase; letter-spacing: .3pt; }
        .fisa-alert ul { margin: 3pt 0 0 16pt; padding: 0; }
        .fisa-alert li { margin: 1pt 0; }
        .fisa-empty { color: #666; font-style: italic; font-size: 10pt; }
        .fisa-section { margin: 5pt 0 8pt; }
        .fisa-note { font-size: 9pt; color: #555; margin: 2pt 0 5pt; }
        .fisa-signature { display: flex; justify-content: space-between; gap: 20pt; margin-top: 14pt; }
        .fisa-sig-col { flex: 1; text-align: center; }
        .fisa-sig-line { border-bottom: 1pt solid #333; height: 24pt; margin: 3pt 12pt 2pt; }
        .fisa-sig-label { font-size: 9pt; }
        .page-break { page-break-before: always; }
      `}</style>

      <div className="fisa-wrap">
        <div className="fisa-print-actions">
          <button onClick={() => window.print()} data-testid="fisa-print-btn">
            Printează (Ctrl+P)
          </button>
        </div>

        <H1>Fișă medico-sportivă — Completare manuală</H1>
        <div className="fisa-sub">Se completează cu pixul, apoi se încarcă scanată în aplicație pentru extragere automată.</div>

        {/* Datele personale */}
        <H2>1. Date personale</H2>
        <div className="fisa-grid-2 fisa-section">
          <Row label="Nume" w="50%" />
          <Row label="Prenume" w="50%" />
          <Row label="CNP" w="60%" />
          <Row label="Data nașterii" w="40%" />
          <Row label="Sex" w="40%" />
          <Row label="Telefon" w="50%" />
          <Row label="Adresă" w="80%" />
          <Row label="Localitate" w="50%" />
        </div>
        <div className="fisa-note">
          Pre-completare (informații existente în dosar):{" "}
          <strong>{dashIfEmpty(dp.nume)} {dashIfEmpty(dp.prenume)}</strong> · CNP{" "}
          <strong>{dashIfEmpty(dp.cnp)}</strong> · Sex{" "}
          <strong>{dashIfEmpty(dp.sex)}</strong> · Data nașterii{" "}
          <strong>{dashIfEmpty(dp.data_nasterii)}</strong>
        </div>

        {/* Info sportive */}
        <H2>2. Informații sportive</H2>
        <div className="fisa-grid-2 fisa-section">
          <Row label="Sport practicat" w="60%" />
          <Row label="Categorie / Post" w="60%" />
          <Row label="Club sportiv" w="60%" />
          <Row label="Antrenor" w="60%" />
          <Row label="Antrenamente / săpt." w="40%" />
          <Row label="Vechime sportivă" w="40%" />
        </div>
        <div className="fisa-note">
          Existent: sport <strong>{dashIfEmpty(is.sport_practicat)}</strong>, club{" "}
          <strong>{dashIfEmpty(is.club_sportiv)}</strong>, antrenor{" "}
          <strong>{dashIfEmpty(is.antrenor)}</strong>.
        </div>

        {/* Antecedente familiale — alertă doar când Da */}
        <H2>3. Antecedente familiale</H2>
        {antecedFamilie.length > 0 ? (
          <div className="fisa-alert">
            <div className="fisa-alert-title">⚠ Semnalări din formular (marcate &bdquo;Da&rdquo;)</div>
            <ul>
              {antecedFamilie.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="fisa-empty">Nu au fost declarate antecedente familiale în formular.</div>
        )}
        <div className="fisa-section">
          <Row label="Observații suplimentare examen" w="90%" />
          <Row label="Observații suplimentare examen" w="90%" />
        </div>

        {/* Antecedente personale */}
        <H2>4. Antecedente personale / stare de sănătate</H2>
        {antecedPersonale.length > 0 ? (
          <div className="fisa-alert">
            <div className="fisa-alert-title">⚠ Semnalări din formular (marcate &bdquo;Da&rdquo;)</div>
            <ul>
              {antecedPersonale.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="fisa-empty">Nu au fost declarate probleme personale în formular.</div>
        )}
        <div className="fisa-section">
          <Row label="Observații examen" w="90%" />
          <Row label="Observații examen" w="90%" />
        </div>

        {/* Efort fizic */}
        <H2>5. Toleranță la efort — simptome declarate</H2>
        {efortFlags.length > 0 ? (
          <div className="fisa-alert">
            <div className="fisa-alert-title">⚠ Simptome la efort declarate (marcate &bdquo;Da&rdquo;)</div>
            <ul>
              {efortFlags.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="fisa-empty">Nu au fost declarate simptome la efort în formular.</div>
        )}

        {/* ================= ATELIERE ================== */}
        <div className="page-break" />

        {/* Atelier 1 */}
        <H2>Atelier 1 — Dezvoltare fizică și evaluare posturală</H2>
        <H3>Antropometrie</H3>
        <div className="fisa-grid-3 fisa-section">
          <Row label="Talie (cm)" w="40%" />
          <Row label="Greutate (kg)" w="40%" />
          <Row label="IMC" w="40%" />
          <Row label="Masă activă (kg)" w="40%" />
          <Row label="Țesut adipos (kg)" w="40%" />
          <Row label="Vârstă" w="40%" />
        </div>

        <H3>Somatoscopie / evaluare posturală</H3>
        <div className="fisa-section">
          <Row label="Constituție (astenică/normostenică/hipertenică/atletică)" w="60%" />
          <Row label="Sistem osos, articular, muscular, adipos" w="80%" />
          <Row label="Tulburări de statică vertebrală (cifoză/scolioză/lordoză)" w="80%" />
          <Row label="Asimetrie umeri / torace / membre inferioare" w="80%" />
          <Row label="Genu valgum / varum / picior addus" w="80%" />
          <Row label="Recomandări posturale" w="90%" />
          <Row label="Observații suplimentare" w="90%" />
        </div>

        <H3>Indici sintetici</H3>
        <div className="fisa-grid-2 fisa-section">
          <Row label="I.F.M.A" w="40%" />
          <Row label="I.F.G" w="40%" />
        </div>

        <div className="page-break" />

        {/* Atelier 2 */}
        <H2>Atelier 2 — Capacitate de efort</H2>
        <H3>Testul Ruffier</H3>
        <div className="fisa-grid-3 fisa-section">
          <Row label="P1 (repaus)" w="40%" />
          <Row label="P2 (după 30 genuflexiuni)" w="40%" />
          <Row label="P3 (revenire)" w="40%" />
          <Row label="Rezultat / Indice" w="40%" />
          <Row label="Calificativ" w="60%" />
          <Row label="TA repaus" w="40%" />
        </div>
        <div className="fisa-section">
          <span className="fisa-cb"><span className="cb-box" /><span className="cb-lbl">Revenire bună</span></span>
          <span className="fisa-cb"><span className="cb-box" /><span className="cb-lbl">Revenire slabă</span></span>
          <span className="fisa-cb"><span className="cb-box" /><span className="cb-lbl">Reevaluare probă efort în</span></span>
          <Line w="60pt" /> <span>zile</span>
        </div>

        <H3>Testul Astrand (VO₂)</H3>
        <div className="fisa-grid-3 fisa-section">
          <Row label="P6' (puls la 6 min)" w="40%" />
          <Row label="Watts / Sarcină" w="40%" />
          <Row label="VO₂ max" w="40%" />
          <Row label="VO₂ relativ (ml/kg/min)" w="40%" />
          <Row label="Calificativ" w="60%" />
          <Row label="Recomandare revenire" w="60%" />
        </div>

        <div className="page-break" />

        {/* Atelier 3 */}
        <H2>Atelier 3 — Examen cardiologic (EKG)</H2>
        <div className="fisa-section">
          <div className="fisa-section">
            <span className="fisa-cb"><span className="cb-box" /><span className="cb-lbl">Traseu EKG în limite normale</span></span>
            <span className="fisa-label" style={{ marginLeft: 10 }}>AV:</span> <Line w="60pt" /> bpm
          </div>
          <Row label="Ritm" w="40%" />
          <Row label="Ax QRS / Interval PR / QT" w="60%" />
          <Row label="Modificări segment ST / T" w="80%" />
          <Row label="Alte observații EKG" w="90%" />
          <div className="fisa-section" style={{ marginTop: 4 }}>
            <span className="fisa-cb"><span className="cb-box" /><span className="cb-lbl">Revenire EKG la</span></span>
            <Line w="60pt" /> <span>zile</span>
          </div>
        </div>

        <div className="page-break" />

        {/* Atelier 4 */}
        <H2>Atelier 4 — Examen clinic pe aparate</H2>
        <div className="fisa-section" style={{ marginBottom: 8 }}>
          <span className="fisa-cb"><span className="cb-box" /><span className="cb-lbl" style={{ fontWeight: 700 }}>Asimptomatic</span></span>
          <span className="fisa-cb"><span className="cb-box" /><span className="cb-lbl" style={{ fontWeight: 700 }}>Normal clinic</span></span>
          <div className="fisa-note">Dacă se bifează una dintre variantele de mai sus, atelierul poate fi finalizat rapid — restul câmpurilor se completează doar dacă există modificări.</div>
        </div>
        <div className="fisa-section">
          <Row label="Tegumente și mucoase" w="80%" />
          <Row label="Sistem ganglionar" w="80%" />
          <Row label="Sistem locomotor" w="80%" />
          <Row label="Aparat respirator" w="80%" />
          <Row label="Aparat cardiovascular (TA, AV, sufluri)" w="80%" />
          <Row label="Aparat digestiv" w="80%" />
          <Row label="Aparat uro-genital" w="80%" />
          <Row label="Sistem endocrin" w="80%" />
          <Row label="Vedere cromatică (Normal / Discromat)" w="60%" />
          <Row label="Acuitate vizuală (necesită corecție optică?)" w="80%" />
          <Row label="Examenul cavității orale (carii, amigdale, aparat ortodontic)" w="80%" />
          <Row label="Observații suplimentare" w="90%" />
        </div>

        <div className="page-break" />

        {/* Atelier 5 */}
        <H2>Atelier 5 — Analize medicale</H2>
        <div className="fisa-note">Se atașează separat rezultatele de laborator; secțiunea de mai jos e pentru concluzii/recomandări scrise manual.</div>
        <div className="fisa-section">
          <Row label="Concluzii pe baza analizelor" w="90%" />
          <Row label="Concluzii pe baza analizelor" w="90%" />
          <Row label="Recomandări (nutriție, refacere, tratament)" w="90%" />
          <Row label="Recomandări (nutriție, refacere, tratament)" w="90%" />
        </div>
        <H3>Sindroame biologice — bifează dacă au fost detectate</H3>
        <div className="fisa-section">
          <CheckBox label="Sindrom de citoliza hepatică (TGO/TGP crescute)" />
          <br />
          <CheckBox label="Sindrom inflamator biologic (VSH crescut)" />
          <br />
          <CheckBox label="Sindrom de rhabdomioliza de suprasolicitare (hiperCKemie izolată de efort)" />
          <br />
          <CheckBox label="Sindrom anemic / suspiciune beta-talasemie minoră" />
        </div>

        {/* Verdict + semnături */}
        <div className="page-break" />
        <H2>Concluzii și verdict</H2>
        <div className="fisa-section">
          <span className="fisa-cb"><span className="cb-box" /><span className="cb-lbl">APT</span></span>
          <span className="fisa-cb"><span className="cb-box" /><span className="cb-lbl">APT condiționat</span></span>
          <span className="fisa-cb"><span className="cb-box" /><span className="cb-lbl">APT individual</span></span>
          <span className="fisa-cb"><span className="cb-box" /><span className="cb-lbl">INAPT temporar</span></span>
        </div>
        <div className="fisa-section">
          <Row label="Diagnostic / Concluzii" w="90%" />
          <Row label="Diagnostic / Concluzii" w="90%" />
          <Row label="Recomandări finale" w="90%" />
          <Row label="Recomandări finale" w="90%" />
        </div>

        <div className="fisa-signature">
          <div className="fisa-sig-col">
            <div className="fisa-sig-line" />
            <div className="fisa-sig-label">Semnătură medic examinator</div>
          </div>
          <div className="fisa-sig-col">
            <div className="fisa-sig-line" />
            <div className="fisa-sig-label">Data completării</div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrintableFisa;
